import React, { useState } from 'react';
import { Zap, TrendingUp, Settings, Info, Clock } from 'lucide-react';
import WalletButton from './components/WalletButton';
import TransactionHistory from './components/TransactionHistory';
import StatsPanel from './components/StatsPanel';
import { HowToPlayButton } from './components/HowToPlay';
import { useWallet } from './hooks/useWallet';
import { useBalance } from './hooks/useBalance';
import { useMining } from './hooks/useMining';
import { useTransactions } from './hooks/useTransactions';
import { formatSOL } from './utils/blockchainUtils';
import { TransactionStatus } from './utils/transactionSimulator';
import logo from './logo.png';

export default function MinerSupplyUI() {
    const { connected, shortAddress } = useWallet();
    const { balance: solBalance, loading: balanceLoading } = useBalance();
    const {
        currentRound,
        timeLeft,
        lastWinningBlock,
        motherlodePot,
        unrefinedMINER,
        refinedMINER,
        pendingSOLRewards,
        selectedBlocks,
        deployedBlocks,
        winRate,
        totalMined,
        roi,
        recentRounds,
        deployToBlocks,
        claimRewards,
        toggleBlockSelection,
        canDeploy,
    } = useMining();

    const {
        transactions,
        isProcessing,
        executeDeploy,
        executeClaim,
    } = useTransactions();

    const [solPerBlock, setSolPerBlock] = useState(0.1);
    const [numBlocks, setNumBlocks] = useState(5);
    const [numRounds, setNumRounds] = useState(60);
    const [autoMinerActive, setAutoMinerActive] = useState(false);

    const blocks = Array.from({ length: 25 }, (_, i) => i + 1);

    const totalSolNeeded = solPerBlock * numBlocks * numRounds;

    const handleDeploy = async () => {
        if (canDeploy && !isProcessing) {
            const blocksArray = Array.from(selectedBlocks);

            // Execute transaction simulation
            const result = await executeDeploy(totalSolNeeded, blocksArray);

            // Only deploy if transaction succeeded
            if (result.status === TransactionStatus.SUCCESS) {
                deployToBlocks(blocksArray, solPerBlock, numRounds);
                setAutoMinerActive(true);
            }
        }
    };

    const handleClaimAll = async () => {
        if (!isProcessing && (unrefinedMINER > 0 || pendingSOLRewards > 0)) {
            // Execute transaction simulation
            const result = await executeClaim(unrefinedMINER, pendingSOLRewards);

            // Only claim if transaction succeeded
            if (result.status === TransactionStatus.SUCCESS) {
                claimRewards(true, true);
            }
        }
    };

    const handleClaimSOLOnly = async () => {
        if (!isProcessing && pendingSOLRewards > 0) {
            // Execute transaction simulation
            const result = await executeClaim(0, pendingSOLRewards);

            // Only claim if transaction succeeded
            if (result.status === TransactionStatus.SUCCESS) {
                claimRewards(false, true);
            }
        }
    };

    return (
        <div className="min-h-screen bg-black text-white overflow-x-hidden">
            {!connected ? (
                    <div className="relative">
                        {/* Animated Background */}
                        <div className="fixed inset-0 grid-bg opacity-30"></div>
                        <div className="fixed inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-orange-900/20"></div>

                        {/* Content */}
                        <div className="relative z-10">
                            {/* Minimal Header */}
                            <header className="absolute top-0 left-0 right-0 z-50">
                                <div className="container mx-auto px-6 py-6 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={logo}
                                            alt="MINER Logo"
                                            className="w-10 h-10 rounded-lg glow-animation"
                                        />
                                        <span className="text-xl font-bold">MINER</span>
                                    </div>
                                    <WalletButton />
                                </div>
                            </header>

                        {/* Hero Section */}
                        <section className="min-h-screen flex items-center justify-center px-6 py-20">
                            <div className="container mx-auto max-w-6xl">
                                <div className="grid lg:grid-cols-2 gap-12 items-center">
                                    {/* Left Side - Text */}
                                    <div>
                                        <div className="inline-flex items-center gap-2 px-4 py-2 glass-card rounded-full text-sm mb-6">
                                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                            <span className="text-green-400">Live on Solana</span>
                                        </div>

                                        <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
                                            Mine<br/>
                                            <span className="gradient-text">Digital Gold</span><br/>
                                            Every Minute
                                        </h1>

                                        <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                                            Deploy SOL. Win blocks. Earn MINER tokens.<br/>
                                            Fair launch. No pre-mine. Pure competition.
                                        </p>

                                        <div className="flex flex-col sm:flex-row gap-4 mb-12">
                                            <WalletButton />
                                            <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })} className="px-8 py-4 glass-card rounded-full font-semibold text-lg hover:bg-white/10 transition-all">
                                                Learn How
                                            </button>
                                        </div>

                                        {/* Quick Stats */}
                                        <div className="grid grid-cols-3 gap-4">
                                            <div>
                                                <div className="text-2xl font-bold gradient-text">60s</div>
                                                <div className="text-xs text-gray-500">Rounds</div>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold gradient-text">5M</div>
                                                <div className="text-xs text-gray-500">Max Supply</div>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold gradient-text">2.8K</div>
                                                <div className="text-xs text-gray-500">Miners</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Side - Visual Grid Demo */}
                                    <div className="float-animation">
                                        <div className="glass-card rounded-3xl p-8">
                                            <div className="text-sm text-gray-400 mb-4 flex items-center justify-between">
                                                <span>Mining Grid</span>
                                                <span className="text-green-400">● Live</span>
                                            </div>

                                            {/* Simplified 5x5 Grid */}
                                            <div className="grid grid-cols-5 gap-2 mb-6">
                                                {[...Array(25)].map((_, i) => {
                                                    const deployed = [2, 6, 12, 17, 24].includes(i);
                                                    const winner = i === 12;
                                                    return (
                                                        <div key={i} className={`aspect-square rounded-lg ${
                                                            winner ? 'bg-green-500/30 border-2 border-green-400 glow-animation flex items-center justify-center text-xs' :
                                                            deployed ? 'bg-blue-500/20 border-2 border-blue-400' :
                                                            'bg-gray-800/50 border border-gray-700'
                                                        }`}>
                                                            {winner && '👑'}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className="flex items-center justify-between text-sm">
                                                <div className="flex items-center gap-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-3 h-3 rounded bg-blue-400"></div>
                                                        <span className="text-gray-400">Your Blocks</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-3 h-3 rounded bg-green-400"></div>
                                                        <span className="text-gray-400">Winner</span>
                                                    </div>
                                                </div>
                                                <div className="text-gray-400">Next: <span className="text-white font-semibold">0:42</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Features Section */}
                        <section className="relative py-20 px-6">
                            <div className="container mx-auto max-w-6xl">
                                <div className="text-center mb-16">
                                    <h2 className="text-4xl md:text-5xl font-bold mb-4">Built Different</h2>
                                    <p className="text-xl text-gray-400">Everything you need to mine successfully</p>
                                </div>

                                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    <div className="glass-card feature-card rounded-2xl p-6">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-2xl mb-4">
                                            ⚡
                                        </div>
                                        <h3 className="text-lg font-bold mb-2">Lightning Fast</h3>
                                        <p className="text-sm text-gray-400">60-second rounds mean constant action and quick rewards</p>
                                    </div>

                                    <div className="glass-card feature-card rounded-2xl p-6">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-2xl mb-4">
                                            🎲
                                        </div>
                                        <h3 className="text-lg font-bold mb-2">Provably Fair</h3>
                                        <p className="text-sm text-gray-400">On-chain randomness ensures fair block selection</p>
                                    </div>

                                    <div className="glass-card feature-card rounded-2xl p-6">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-2xl mb-4">
                                            💎
                                        </div>
                                        <h3 className="text-lg font-bold mb-2">Motherlode</h3>
                                        <p className="text-sm text-gray-400">Hit the jackpot for massive bonus rewards</p>
                                    </div>

                                    <div className="glass-card feature-card rounded-2xl p-6">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-2xl mb-4">
                                            🔐
                                        </div>
                                        <h3 className="text-lg font-bold mb-2">Self-Custody</h3>
                                        <p className="text-sm text-gray-400">Your wallet, your keys, your control</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* How It Works */}
                        <section id="how-it-works" className="relative py-20 px-6">
                            <div className="container mx-auto max-w-4xl">
                                <div className="text-center mb-16">
                                    <h2 className="text-4xl md:text-5xl font-bold mb-4">Simple Process</h2>
                                    <p className="text-xl text-gray-400">Start mining in 3 easy steps</p>
                                </div>

                                <div className="space-y-8">
                                    <div className="glass-card rounded-2xl p-8 flex items-start gap-6">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                                            1
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold mb-2">Connect Your Wallet</h3>
                                            <p className="text-gray-400">Use Phantom, Solflare, or any Solana wallet. Takes seconds.</p>
                                        </div>
                                    </div>

                                    <div className="glass-card rounded-2xl p-8 flex items-start gap-6">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                                            2
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold mb-2">Choose Your Strategy</h3>
                                            <p className="text-gray-400">Select blocks on the 5×5 grid and deploy your SOL. Manual or auto-mine.</p>
                                        </div>
                                    </div>

                                    <div className="glass-card rounded-2xl p-8 flex items-start gap-6">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                                            3
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold mb-2">Win & Earn</h3>
                                            <p className="text-gray-400">Winners split losing blocks' SOL + earn MINER tokens. Claim anytime.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Final CTA */}
                        <section className="relative py-32 px-6">
                            <div className="container mx-auto max-w-4xl text-center">
                                <div className="inline-flex items-center gap-2 px-4 py-2 glass-card rounded-full text-sm mb-8">
                                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                    <span>2,847 active miners right now</span>
                                </div>

                                <h2 className="text-5xl md:text-6xl font-bold mb-6">
                                    Ready to<br/>
                                    <span className="gradient-text">Strike Gold?</span>
                                </h2>

                                <p className="text-xl text-gray-400 mb-10">
                                    Join the mining revolution on Solana
                                </p>

                                <div className="flex justify-center">
                                    <WalletButton />
                                </div>

                                <p className="text-sm text-gray-500 mt-6">No fees to start • Mine from any device</p>
                            </div>
                        </section>

                        {/* Minimal Footer */}
                        <footer className="relative border-t border-white/5 py-8 px-6">
                            <div className="container mx-auto max-w-6xl">
                                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                    <div className="flex items-center gap-2">
                                        <img src={logo} alt="MINER Logo" className="w-8 h-8 rounded-lg" />
                                        <span className="font-bold">MINER</span>
                                        <span className="text-gray-600">•</span>
                                        <span className="text-gray-400 text-sm">Built on Solana</span>
                                    </div>
                                    <div className="flex items-center gap-6 text-sm text-gray-400">
                                        <HowToPlayButton />
                                        <a href="#" className="hover:text-white transition-colors">Twitter</a>
                                        <a href="#" className="hover:text-white transition-colors">Discord</a>
                                        <a href="#" className="hover:text-white transition-colors">GitHub</a>
                                    </div>
                                </div>
                            </div>
                        </footer>
                        </div>
                    </div>
                ) : (
                    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
                        {/* Header - Connected State */}
                        <header className="border-b border-purple-500/20 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
                            <div className="container mx-auto px-6 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <img
                                        src={logo}
                                        alt="MINER Logo"
                                        className="w-10 h-10 rounded-lg"
                                    />
                                    <span className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                                        MINER
                                    </span>
                                </div>

                                <nav className="hidden md:flex items-center gap-6 text-sm">
                                    <a href="#" className="hover:text-orange-400 transition-colors">Mine</a>
                                    <a href="#" className="hover:text-orange-400 transition-colors">Stake</a>
                                    <HowToPlayButton />
                                    <a href="#" className="hover:text-orange-400 transition-colors">Analytics</a>
                                </nav>

                                <WalletButton />
                            </div>
                        </header>

                        <main className="container mx-auto px-6 py-8">
                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Left Sidebar - Stats */}
                        <div className="lg:col-span-1 space-y-6">
                            {/* Your Balance */}
                            <div className="bg-black/40 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6">
                                <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2">
                                    <TrendingUp size={16} />
                                    YOUR BALANCE
                                </h3>

                                <div className="space-y-4">
                                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-2">
                                        <div className="text-xs text-gray-400 mb-1">Wallet SOL Balance</div>
                                        <div className="text-2xl font-bold text-blue-400">
                                            {balanceLoading ? 'Loading...' : `${formatSOL(solBalance)} SOL`}
                                        </div>
                                        {shortAddress && (
                                            <div className="text-xs text-gray-500 mt-1">{shortAddress}</div>
                                        )}
                                    </div>

                                    <div>
                                        <div className="text-xs text-gray-400 mb-1">Unrefined MINER</div>
                                        <div className="text-3xl font-bold text-orange-400">{unrefinedMINER.toFixed(3)}</div>
                                        <div className="text-xs text-gray-500 mt-1">Claim with 10% refining fee</div>
                                    </div>

                                    <div>
                                        <div className="text-xs text-gray-400 mb-1">Refined MINER</div>
                                        <div className="text-2xl font-bold text-green-400">{refinedMINER.toFixed(3)}</div>
                                        <div className="text-xs text-gray-500 mt-1">From other miners' fees</div>
                                    </div>

                                    <div className="border-t border-purple-500/20 pt-4">
                                        <div className="text-xs text-gray-400 mb-1">Pending SOL Rewards</div>
                                        <div className="text-xl font-bold">{formatSOL(pendingSOLRewards)} SOL</div>
                                    </div>

                                    <div>
                                        <div className="text-xs text-gray-400 mb-1">Total Value (approx)</div>
                                        <div className="text-xl font-bold text-purple-400">
                                            {formatSOL((unrefinedMINER + refinedMINER) * 0.1 + pendingSOLRewards)} SOL
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 space-y-2">
                                    <button
                                        onClick={handleClaimAll}
                                        disabled={unrefinedMINER === 0 && pendingSOLRewards === 0 || isProcessing}
                                        className="w-full py-3 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed rounded-lg font-semibold transition-all"
                                    >
                                        {isProcessing ? 'Processing...' : 'Claim All (10% Fee)'}
                                    </button>
                                    <button
                                        onClick={handleClaimSOLOnly}
                                        disabled={pendingSOLRewards === 0 || isProcessing}
                                        className="w-full py-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition-all"
                                    >
                                        {isProcessing ? 'Processing...' : 'Claim SOL Only'}
                                    </button>
                                </div>
                            </div>

                            {/* Motherlode */}
                            <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-2xl p-6">
                                <h3 className="text-sm font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                                    💎 MOTHERLODE JACKPOT
                                </h3>
                                <div className="text-3xl font-bold text-yellow-300 mb-2">{motherlodePot.toFixed(1)} MINER</div>
                                <div className="text-xs text-gray-300 mb-1">1 in 625 chance per round</div>
                                <div className="text-xs text-gray-400">+0.2 MINER every round</div>
                                <div className="mt-4 h-2 bg-black/30 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-yellow-400 to-orange-500" style={{ width: `${Math.min((motherlodePot / 20) * 100, 100)}%` }}></div>
                                </div>
                            </div>

                            {/* Staking Info */}
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                                <h4 className="font-semibold text-sm mb-2 text-blue-400">⚡ Boost Your Rewards</h4>
                                <p className="text-xs text-gray-300 mb-3">
                                    Stake MINER to earn up to 2x multiplier on mining rewards
                                </p>
                                <button className="w-full py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 rounded-lg text-sm font-semibold transition-all">
                                    Stake MINER
                                </button>
                            </div>

                            {/* Info */}
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 text-sm">
                                <div className="flex items-start gap-2">
                                    <Info size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />
                                    <div className="text-gray-300">
                                        <p className="font-semibold mb-1">How it works:</p>
                                        <ul className="text-xs space-y-1">
                                            <li>• Deploy SOL on blocks</li>
                                            <li>• Random block selected every minute</li>
                                            <li>• Winners split losing blocks' SOL</li>
                                            <li>• 1 MINER distributed to winners</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Center - Mining Grid */}
                        <div className="lg:col-span-2">
                            <div className="bg-black/40 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6 mb-6">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-xl font-bold">Mining Blocks</h3>
                                        <p className="text-sm text-gray-400">Select blocks to deploy SOL</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={autoMinerActive}
                                                onChange={(e) => setAutoMinerActive(e.target.checked)}
                                                className="w-4 h-4 rounded"
                                            />
                                            <span>Auto-Miner</span>
                                        </label>
                                        <Settings size={20} className="text-gray-400 cursor-pointer hover:text-white" />
                                    </div>
                                </div>

                                {/* Round Timer */}
                                <div className="mb-6 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Clock size={16} />
                                            <span className="text-sm font-semibold">Next Round</span>
                                        </div>
                                        <span className="text-2xl font-bold text-green-400">0:{timeLeft < 10 ? '0' : ''}{timeLeft}</span>
                                    </div>
                                    <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-gradient-to-r from-green-400 to-emerald-500"
                                            style={{ width: `${(timeLeft / 60) * 100}%` }}
                                        ></div>
                                    </div>
                                    <div className="mt-2 text-xs text-gray-400 flex justify-between">
                                        <span>Round #{currentRound.toLocaleString()}</span>
                                        <span>Last winner: Block {lastWinningBlock}</span>
                                    </div>
                                </div>

                                {/* Deployment Controls */}
                                <div className="mb-6 grid md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-sm text-gray-400 mb-2 block">SOL per Block</label>
                                        <input
                                            type="number"
                                            value={solPerBlock}
                                            onChange={(e) => setSolPerBlock(parseFloat(e.target.value))}
                                            step="0.01"
                                            min="0.01"
                                            className="w-full bg-purple-500/10 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-400 mb-2 block">Number of Blocks</label>
                                        <input
                                            type="number"
                                            value={numBlocks}
                                            onChange={(e) => setNumBlocks(parseInt(e.target.value))}
                                            min="1"
                                            max="25"
                                            className="w-full bg-purple-500/10 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-400 mb-2 block">Number of Rounds</label>
                                        <input
                                            type="number"
                                            value={numRounds}
                                            onChange={(e) => setNumRounds(parseInt(e.target.value))}
                                            min="1"
                                            className="w-full bg-purple-500/10 border border-purple-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                                        />
                                    </div>
                                </div>

                                <div className="mb-6 bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm text-gray-400">Total SOL Required</div>
                                            <div className="text-2xl font-bold text-orange-400">{totalSolNeeded.toFixed(2)} SOL</div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {numBlocks} blocks × {numRounds} rounds × {solPerBlock} SOL
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleDeploy}
                                            disabled={selectedBlocks.size === 0 || isProcessing}
                                            className="px-8 py-3 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed rounded-lg font-bold transition-all transform hover:scale-105 disabled:hover:scale-100"
                                        >
                                            {isProcessing ? 'Processing...' : 'Deploy SOL'}
                                        </button>
                                    </div>
                                </div>

                                {/* 5x5 Grid */}
                                <div className="grid grid-cols-5 gap-3 mb-6">
                                    {blocks.map((blockNum) => {
                                        const isSelected = selectedBlocks.has(blockNum);
                                        const isDeployed = deployedBlocks.includes(blockNum);
                                        const isWinner = blockNum === lastWinningBlock;

                                        return (
                                            <button
                                                key={blockNum}
                                                onClick={() => toggleBlockSelection(blockNum)}
                                                className={`aspect-square rounded-xl font-bold text-lg transition-all transform hover:scale-105 relative ${
                                                    isWinner
                                                        ? 'bg-gradient-to-br from-green-500 to-emerald-500'
                                                        : isDeployed
                                                        ? 'bg-gradient-to-br from-blue-500 to-cyan-500 border-2 border-blue-300'
                                                        : isSelected
                                                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/50'
                                                        : 'bg-gray-800/50 border border-gray-700 hover:border-purple-500/50'
                                                }`}
                                            >
                                                {blockNum}
                                                {isDeployed && (
                                                    <div className="absolute top-1 right-1 w-2 h-2 bg-blue-300 rounded-full"></div>
                                                )}
                                                {isWinner && (
                                                    <div className="absolute -top-1 -right-1 text-xs">👑</div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Legend */}
                                <div className="flex items-center justify-center gap-6 text-xs text-gray-400">
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-gray-800/50 border border-gray-700"></div>
                                        <span>Available</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-gradient-to-br from-purple-500 to-pink-500"></div>
                                        <span>Selected</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-500 to-cyan-500"></div>
                                        <span>Deployed</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-gradient-to-br from-green-500 to-emerald-500"></div>
                                        <span>Winner</span>
                                    </div>
                                </div>
                            </div>

                            {/* Recent Activity */}
                            <div className="bg-black/40 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6">
                                <h3 className="text-lg font-bold mb-4">Recent Rounds</h3>
                                <div className="space-y-3">
                                    {recentRounds.length === 0 ? (
                                        <div className="text-center text-gray-400 py-8">
                                            No rounds played yet. Deploy SOL to start mining!
                                        </div>
                                    ) : (
                                        recentRounds.map((activity, i) => (
                                            <div
                                                key={i}
                                                className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg hover:bg-gray-800/50 transition-all"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                                                        activity.won ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'
                                                    }`}>
                                                        {activity.winningBlock}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold flex items-center gap-2">
                                                            Round #{activity.roundNumber.toLocaleString()}
                                                            {activity.motherlodeHit && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">💎 MOTHERLODE</span>}
                                                        </div>
                                                        <div className="text-xs text-gray-400">Winner: Block {activity.winningBlock}</div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className={`text-sm font-semibold ${
                                                        activity.won ? 'text-green-400' : 'text-red-400'
                                                    }`}>
                                                        {activity.won ? `+${formatSOL(activity.solReward)}` : '-'} SOL
                                                    </div>
                                                    <div className="text-xs text-orange-400">
                                                        +{(activity.minerReward + activity.motherlodeReward).toFixed(3)} MINER
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Transaction History */}
                            {transactions.length > 0 && (
                                <div className="mt-6">
                                    <TransactionHistory transactions={transactions} maxItems={5} />
                                </div>
                            )}

                            {/* Stats Summary */}
                            <div className="mt-6">
                                <StatsPanel
                                    winRate={winRate}
                                    totalMined={totalMined}
                                    roi={roi}
                                    totalRounds={recentRounds.length}
                                />
                            </div>
                        </div>
                    </div>
                        </main>

                        {/* Footer - Connected State */}
                        <footer className="border-t border-purple-500/20 bg-black/30 backdrop-blur-sm mt-20">
                <div className="container mx-auto px-6 py-8">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h4 className="font-bold mb-4 text-orange-400">MINER</h4>
                            <p className="text-sm text-gray-400">
                                Fair-launch mining protocol on Solana. Mine, stake, and win.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm">Product</h4>
                            <div className="space-y-2 text-sm text-gray-400">
                                <div><a href="#" className="hover:text-white">Mining</a></div>
                                <div><a href="#" className="hover:text-white">Staking</a></div>
                                <div><a href="#" className="hover:text-white">Trade</a></div>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm">Resources</h4>
                            <div className="space-y-2 text-sm text-gray-400">
                                <div><a href="#" className="hover:text-white">Documentation</a></div>
                                <div><a href="#" className="hover:text-white">Tokenomics</a></div>
                                <div><a href="#" className="hover:text-white">GitHub</a></div>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm">Community</h4>
                            <div className="space-y-2 text-sm text-gray-400">
                                <div><a href="#" className="hover:text-white">Twitter</a></div>
                                <div><a href="#" className="hover:text-white">Discord</a></div>
                                <div><a href="#" className="hover:text-white">Telegram</a></div>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-purple-500/20 pt-6 text-center text-sm text-gray-400">
                        <p>© 2025 MINER. Fair Launch • Community Driven • Built on Solana.</p>
                    </div>
                </div>
            </footer>
                    </div>
                )}
        </div>
    );
}